import { FC, useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { QcsFormControl } from './basic/QcsFormControl';
import { QcsOutlinedInput } from './basic/QcsOutlinedInput';
import { debounce, InputAdornment as QcsInputAdornment } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { DEBOUNCE_TIMEOUT_MS } from '../../utils/constants';
import styled from '@emotion/styled';

const StyledQcsOutlinedInput = styled(QcsOutlinedInput)(() => ({
  maxHeight: '2.5rem',
}));

interface Props {
  className?: string;
  defaultValue: string;
  onChanged: (value: string) => void;
}

export const Search: FC<Props> = ({ className, defaultValue, onChanged }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(defaultValue);
  const [prevDefaultValue, setPrevDefaultValue] = useState(defaultValue);

  // Derived state: when defaultValue is cleared externally (e.g. navigating to a different entity),
  // reset the input. React allows setState during render when guarded by a prev-value comparison.
  if (prevDefaultValue !== defaultValue) {
    setPrevDefaultValue(defaultValue);
    if (!defaultValue) setValue('');
  }

  // Keep onChanged reachable from the stable debounced function without re-creating it.
  const onChangedRef = useRef(onChanged);
  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  // Initialize debounce in an effect so ref.current is never accessed during render.
  const debouncedRef = useRef<((v: string) => void) | null>(null);
  useEffect(() => {
    debouncedRef.current = debounce((v: string) => onChangedRef.current(v), DEBOUNCE_TIMEOUT_MS);
  }, []);

  return (
    <QcsFormControl className={className}>
      <StyledQcsOutlinedInput
        type="text"
        placeholder={t('common.search')}
        autoComplete="off"
        value={value}
        onChange={(event) => {
          const v = event.target.value;
          setValue(v);
          debouncedRef.current?.(v);
        }}
        endAdornment={
          <QcsInputAdornment position="end">
            <SearchIcon />
          </QcsInputAdornment>
        }
      />
    </QcsFormControl>
  );
};
